import React, { useState } from 'react';
import {
  Card, Input, Button, Typography, Tag, Space, Alert, Spin,
  Row, Col, Divider, Avatar, Statistic, Badge, Tooltip,
} from 'antd';
import {
  RobotOutlined, SendOutlined, EnvironmentOutlined,
  StarFilled, ThunderboltOutlined, CheckCircleOutlined,
  ClockCircleOutlined, TeamOutlined, DollarOutlined,
} from '@ant-design/icons';
import { publicApi } from '../api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface ProviderRanking {
  id: number;
  userId: number;
  name: string;
  bio: string;
  location: string;
  latitude: number;
  longitude: number;
  distanceKm: number;
  rating: number;
  completedJobs: number;
  hourlyRate: number;
  isVerified: boolean;
  isFeatured: boolean;
}

interface DispatchResponse {
  intent: string;
  serviceType: string;
  categoryId: number;
  urgency: string;
  providers: ProviderRanking[];
}

const SAMPLE_QUERIES = [
  'My pipes are leaking and flooding the kitchen, need a plumber urgently in Sandton',
  'Power tripping at home, need an electrician in Braamfontein today',
  'Car won\'t start, need a mechanic in Germiston as soon as possible',
  'Need my garden mowed and pruned, Soweto area',
  'House needs a deep clean before guests arrive this weekend',
];

const urgencyColor: Record<string, string> = { high: 'red', med: 'orange', low: 'green' };
const urgencyIcon: Record<string, React.ReactNode> = {
  high: <ThunderboltOutlined />,
  med: <ClockCircleOutlined />,
  low: <CheckCircleOutlined />,
};

export default function AiDispatcher() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DispatchResponse | null>(null);
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState(0);

  const handleDispatch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    const t0 = Date.now();

    try {
      const res = await publicApi.post('/agent/dispatch', {
        query,
        location: { latitude: -26.2041, longitude: 28.0473 }, // JHB CBD default
      });
      setResult(res.data);
      setElapsed(Date.now() - t0);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to dispatch');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 8px' }}>
      {/* Header */}
      <Space align="center" style={{ marginBottom: 24 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: 'linear-gradient(135deg,#1677ff,#7c3aed)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <RobotOutlined style={{ fontSize: 24, color: '#fff' }} />
        </div>
        <div>
          <Title level={3} style={{ margin: 0 }}>AI Dispatcher Console</Title>
          <Text type="secondary">Natural language → intent extraction → ranked provider list</Text>
        </div>
      </Space>

      <Row gutter={[24, 24]}>
        {/* Input card */}
        <Col span={24}>
          <Card
            style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,.06)' }}
            bodyStyle={{ padding: 24 }}
          >
            <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
              Simulate Customer Request
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              Type a natural language request — the AI will extract the service type, urgency
              level, and return the closest available providers ranked by distance.
            </Text>

            {/* Sample query chips */}
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>Try:</Text>
              {SAMPLE_QUERIES.map((q, i) => (
                <Tag
                  key={i}
                  style={{ cursor: 'pointer', marginBottom: 6, borderRadius: 12 }}
                  onClick={() => setQuery(q)}
                >
                  {q.length > 55 ? q.substring(0, 55) + '…' : q}
                </Tag>
              ))}
            </div>

            <TextArea
              rows={5}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onPressEnter={e => { if (e.ctrlKey) handleDispatch(); }}
              placeholder="Describe the customer's issue in plain English…"
              disabled={loading}
              style={{ fontSize: 15, padding: 14, borderRadius: 8, resize: 'vertical' }}
            />

            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Ctrl+Enter to dispatch · Location defaults to JHB CBD
              </Text>
              <Button
                type="primary"
                icon={<SendOutlined />}
                size="large"
                onClick={handleDispatch}
                loading={loading}
                disabled={!query.trim()}
                style={{ borderRadius: 8, paddingInline: 28 }}
              >
                Run AI Dispatcher
              </Button>
            </div>
          </Card>
        </Col>

        {/* Error */}
        {error && (
          <Col span={24}>
            <Alert message="Dispatch Failed" description={error} type="error" showIcon closable onClose={() => setError('')} />
          </Col>
        )}

        {/* Loading */}
        {loading && (
          <Col span={24}>
            <Card style={{ borderRadius: 12, textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Analysing request with AI · matching providers…</Text>
              </div>
            </Card>
          </Col>
        )}

        {/* Results */}
        {result && !loading && (
          <>
            {/* Intent summary */}
            <Col span={24}>
              <Card
                style={{ borderRadius: 12, background: 'linear-gradient(135deg,#f0f7ff,#f9f0ff)', border: '1px solid #bae0ff' }}
                bodyStyle={{ padding: 24 }}
              >
                <Row gutter={24} align="middle">
                  <Col flex="auto">
                    <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
                      AI Intent
                    </Text>
                    <Title level={5} style={{ margin: '4px 0 0' }}>{result.intent}</Title>
                  </Col>
                  <Col>
                    <Space size={16}>
                      <Statistic
                        title="Service"
                        value={result.serviceType}
                        valueStyle={{ fontSize: 16, color: '#1677ff' }}
                        prefix={<TeamOutlined />}
                      />
                      <Divider type="vertical" style={{ height: 40 }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>Urgency</Text>
                        <div style={{ marginTop: 4 }}>
                          <Tag
                            icon={urgencyIcon[result.urgency]}
                            color={urgencyColor[result.urgency] || 'blue'}
                            style={{ fontSize: 13, padding: '2px 10px' }}
                          >
                            {result.urgency?.toUpperCase()}
                          </Tag>
                        </div>
                      </div>
                      <Divider type="vertical" style={{ height: 40 }} />
                      <Statistic
                        title="Matched"
                        value={result.providers.length}
                        suffix="providers"
                        valueStyle={{ fontSize: 16, color: result.providers.length > 0 ? '#52c41a' : '#ff4d4f' }}
                        prefix={elapsed > 0 ? undefined : undefined}
                      />
                      {elapsed > 0 && (
                        <>
                          <Divider type="vertical" style={{ height: 40 }} />
                          <Statistic title="Time" value={(elapsed / 1000).toFixed(2)} suffix="s" valueStyle={{ fontSize: 16 }} />
                        </>
                      )}
                    </Space>
                  </Col>
                </Row>
              </Card>
            </Col>

            {/* Provider cards */}
            <Col span={24}>
              <Title level={5} style={{ marginBottom: 12 }}>
                Available Providers
                {result.providers.length > 0 && (
                  <Text type="secondary" style={{ fontSize: 13, fontWeight: 400, marginLeft: 8 }}>
                    Ranked by distance · within 25km
                  </Text>
                )}
              </Title>

              {result.providers.length === 0 ? (
                <Alert
                  message="No providers found within 25km"
                  description="Try a different query or expand the search area."
                  type="warning"
                  showIcon
                />
              ) : (
                <Row gutter={[16, 16]}>
                  {result.providers.map((p, i) => (
                    <Col key={p.id} xs={24} sm={12} lg={8}>
                      <Card
                        style={{
                          borderRadius: 12,
                          boxShadow: i === 0 ? '0 0 0 2px #1677ff' : '0 2px 8px rgba(0,0,0,.08)',
                          position: 'relative',
                        }}
                        bodyStyle={{ padding: 20 }}
                      >
                        {i === 0 && (
                          <div style={{
                            position: 'absolute', top: -1, right: 12,
                            background: '#1677ff', color: '#fff',
                            fontSize: 11, padding: '2px 10px', borderRadius: '0 0 8px 8px',
                            fontWeight: 600, letterSpacing: 0.5,
                          }}>
                            BEST MATCH
                          </div>
                        )}

                        <Space style={{ marginBottom: 12 }}>
                          <Avatar
                            size={48}
                            style={{ background: `hsl(${(p.id * 47) % 360},60%,55%)`, fontWeight: 700, fontSize: 18 }}
                          >
                            {p.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                          </Avatar>
                          <div>
                            <Space>
                              <Text strong style={{ fontSize: 15 }}>{p.name}</Text>
                              {p.isVerified && (
                                <Tooltip title="Verified Provider">
                                  <CheckCircleOutlined style={{ color: '#1677ff', fontSize: 14 }} />
                                </Tooltip>
                              )}
                              {p.isFeatured && <Tag color="gold" style={{ fontSize: 10 }}>Featured</Tag>}
                            </Space>
                            <div>
                              {p.rating > 0 && (
                                <Space size={4}>
                                  <StarFilled style={{ color: '#faad14', fontSize: 12 }} />
                                  <Text style={{ fontSize: 13 }}>{p.rating?.toFixed(1)}</Text>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    ({p.completedJobs} jobs)
                                  </Text>
                                </Space>
                              )}
                            </div>
                          </div>
                        </Space>

                        {p.bio && (
                          <Paragraph
                            type="secondary"
                            style={{ fontSize: 12, marginBottom: 12 }}
                            ellipsis={{ rows: 2 }}
                          >
                            {p.bio}
                          </Paragraph>
                        )}

                        <Row gutter={8}>
                          <Col span={12}>
                            <div style={{ background: '#f6f9ff', borderRadius: 8, padding: '8px 10px' }}>
                              <Text type="secondary" style={{ fontSize: 11 }}>DISTANCE</Text>
                              <div>
                                <Text strong style={{ color: '#1677ff' }}>
                                  {p.distanceKm < 1
                                    ? `${Math.round(p.distanceKm * 1000)}m`
                                    : `${p.distanceKm.toFixed(1)}km`}
                                </Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={{ background: '#f6fff6', borderRadius: 8, padding: '8px 10px' }}>
                              <Text type="secondary" style={{ fontSize: 11 }}>RATE</Text>
                              <div>
                                <Text strong style={{ color: '#52c41a' }}>
                                  R{p.hourlyRate?.toFixed(0)}/hr
                                </Text>
                              </div>
                            </div>
                          </Col>
                        </Row>

                        {p.location && (
                          <div style={{ marginTop: 10 }}>
                            <Space size={4}>
                              <EnvironmentOutlined style={{ color: '#888', fontSize: 12 }} />
                              <Text type="secondary" style={{ fontSize: 12 }}>{p.location}</Text>
                            </Space>
                          </div>
                        )}
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Col>
          </>
        )}
      </Row>
    </div>
  );
}
